import React, { useEffect, useRef } from 'react';

export interface OtpInputProps {
  length?: number; // default 6
  disabled?: boolean;
  onComplete: (code: string) => void;
  className?: string;
}

/* --------------------------------------------------------------------------
 * Very small utility component rendering N separate <input> fields that accept
 * only numeric digits.  As soon as all boxes are filled `onComplete` is fired
 * with the concatenated code.
 *
 * • Auto-advances focus as the user types.  Backspace at position i clears the
 *   current box and moves focus to i-1.
 * • Paste-aware – users can paste the full code into any box and the component
 *   will distribute the digits left-to-right.
 * ------------------------------------------------------------------------*/

export function OtpInput({ length = 6, disabled, onComplete, className }: OtpInputProps) {
  const [values, setValues] = React.useState<string[]>(Array.from({ length }, () => ''));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  // Fire onComplete when all digits entered
  useEffect(() => {
    if (values.every((v) => v.length === 1)) {
      onComplete(values.join(''));
    }
  }, [values, onComplete]);

  const setDigit = (idx: number, char: string) => {
    setValues((arr) => {
      const next = [...arr];
      next[idx] = char;
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length === 0) {
      setDigit(idx, '');
      return;
    }

    if (val.length === 1) {
      setDigit(idx, val);
      // move focus
      if (idx < length - 1) inputs.current[idx + 1]?.focus();
      return;
    }

    // Handle paste of entire code
    const chars = val.slice(0, length).split('');
    const next = Array.from({ length }, (_, i) => chars[i] ?? values[i]);
    setValues(next);
    // move focus to last filled
    const last = chars.length - 1;
    if (last >= 0 && last < length) inputs.current[last]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace' && values[idx] === '') {
      if (idx > 0) inputs.current[idx - 1]?.focus();
    }
  };

  const gridColsClass = `grid-cols-${length}`; // Tailwind safelist; 6 by default.
  return (
    <div className={`grid ${gridColsClass} gap-2 ${className ?? ''}`.trim()}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          type="text"
          inputMode="numeric"
          maxLength={1}
          aria-label={`OTP digit ${i + 1} of ${length}`}
          disabled={disabled}
          value={values[i]}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          ref={(el) => {
            inputs.current[i] = el;
          }}
          className="h-12 w-full rounded-md border border-gray-300 text-center text-lg focus:border-primary focus:ring-primary disabled:bg-gray-100"
        />
      ))}
    </div>
  );
}

export default OtpInput;
